/**
 * StageView — `/stage/:slug`
 *
 * Wraps the existing curriculum lessons with the owner-specified frame:
 *   1. Pre-Brief (why this stage matters NOW)
 *   2. Pointers (active learning prompts)
 *   3. Core content (lesson list — links to existing /lesson/:id)
 *   4. Reflection (first-person prompts, optional)
 *   5. Next-lesson focus (what to take into the next driving lesson)
 *
 * Plus a dedicated "Responsibility & support" panel (what you own, what's
 * still supported) and a calm Readiness Signal at the end.
 *
 * The existing lesson content is NEVER edited by this view — we only frame it.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Eye, BookOpen, Compass, Heart, ArrowRight,
  CheckCircle2, Circle, Lightbulb, Target, Award, Shield,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StageView() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [frame, setFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reflectionsTouched, setReflectionsTouched] = useState({});
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/stage-frames/${slug}`, { withCredentials: true })
      .then((r) => { if (alive) setFrame(r.data); })
      .catch(() => { if (alive) toast.error("Couldn't load this stage."); })
      .finally(() => { if (alive) setLoading(false); });
    axios
      .get(`${API}/api/learner-framework/stages/${slug}/confirmation/me`, { withCredentials: true })
      .then((r) => { if (alive) setConfirmation(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <p className="text-slate-500 text-sm">Loading…</p>
      </div>
    );
  }
  if (!frame) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 p-6">
        <button onClick={() => nav("/learn")} className="text-sm text-emerald-600">← Back to learn</button>
        <p className="mt-6 text-slate-700">Stage not found.</p>
      </div>
    );
  }

  const lessons = frame.lessons || [];
  const completedCount = lessons.filter((l) => l.completed).length;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-12" data-testid={`stage-view-${slug}`}>
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="stage-back"
            onClick={() => nav("/learn")}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-600 dark:text-emerald-400">Responsibility stage</p>
            <h1 className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white tracking-tight truncate">
              {frame.label}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-6">
        {/* CONFIRMATION BANNER (Phase 1) — surfaces stage verdict status */}
        {confirmation && confirmation.verdict === "ready" && (
          <section data-testid="stage-confirmation-banner-ready" className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-none">✓</div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-700 dark:text-emerald-300">
                Stage confirmed
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                Your instructor confirmed you Ready on this stage. Cert added to your Passport · next stage unlocked.
              </p>
            </div>
          </section>
        )}
        {confirmation && confirmation.verdict === "nearly_there" && (
          <section data-testid="stage-confirmation-banner-nearly" className="rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-amber-700 dark:text-amber-300">
              Almost there
            </p>
            <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
              You've nearly held this stage. One more drive to lock it in — no rush, no shame.
            </p>
            {confirmation.note && (
              <p className="text-[12px] text-slate-700 dark:text-slate-300 mt-1.5">
                <span className="font-extrabold">Carry-forward note:</span> {confirmation.note}
              </p>
            )}
          </section>
        )}
        {confirmation && confirmation.verdict === "not_yet" && (
          <section data-testid="stage-confirmation-banner-not-yet" className="rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-700 dark:text-indigo-300">
              Staying on this stage
            </p>
            <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
              Your instructor will try a different explanation next lesson. Understanding unlocks progression.
            </p>
            {confirmation.note && (
              <p className="text-[12px] text-slate-700 dark:text-slate-300 mt-1.5">
                <span className="font-extrabold">What we'll work on:</span> {confirmation.note}
              </p>
            )}
          </section>
        )}
        {confirmation && !confirmation.verdict && completedCount > 0 && completedCount === lessons.length && lessons.length > 0 && (
          <section data-testid="stage-confirmation-banner-awaiting" className="rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-700 dark:text-indigo-300">
              Awaiting instructor confirmation
            </p>
            <p className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
              All lessons read. Your instructor will confirm your stage on your next lesson — that unlocks the next one.
            </p>
          </section>
        )}

        {/* 1 — PRE-BRIEF */}
        <section data-testid="stage-pre-brief" className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/15 border border-emerald-200 dark:border-emerald-900/40 rounded-3xl p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5 mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Why this stage now
          </p>
          <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-snug" dangerouslySetInnerHTML={{ __html: linkifyBold(frame.pre_brief) }} />
        </section>

        {/* 2 — POINTERS */}
        <section data-testid="stage-pointers">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-3">
            <Eye className="w-4 h-4 text-blue-600" />
            As you go through this, look for…
          </h3>
          <ul className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
            {(frame.pointers || []).map((p, i) => (
              <li key={i} className="px-4 py-3 flex items-start gap-3">
                <span className="text-[11px] font-extrabold text-blue-600 dark:text-blue-400 mt-0.5 w-5">{i + 1}.</span>
                <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed flex-1">{p}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* 3 — CORE CONTENT (existing lessons) */}
        {lessons.length > 0 && (
          <section data-testid="stage-lessons">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-emerald-600" />
                The lessons in this stage
              </h3>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">
                {completedCount} of {lessons.length} read
              </span>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
              {lessons.map((l) => (
                <Link
                  key={l.id}
                  to={`/lesson/${l.id}`}
                  data-testid={`stage-lesson-${l.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {l.completed ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600 flex-shrink-0" />
                  )}
                  <p className="flex-1 text-sm text-slate-900 dark:text-white font-semibold leading-snug">{l.title}</p>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* RESPONSIBILITY & SUPPORT panel */}
        <section data-testid="stage-responsibility-panel" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Panel
            icon={Award}
            tone="amber"
            label="What you hold"
            body={frame.responsibility_held}
          />
          <Panel
            icon={Shield}
            tone="blue"
            label="What's still supported"
            body={frame.support_remaining}
          />
          <Panel
            icon={Compass}
            tone="emerald"
            label="What you're growing now"
            body={frame.growing_now}
          />
          <Panel
            icon={Sparkles}
            tone="rose"
            label="Ready to move on when…"
            body={frame.readiness_signal}
          />
        </section>

        {/* CONFIDENCE-TRANSFER panel — the 5 owner questions */}
        {(frame.proving_what || frame.new_environment || frame.confidence_earned) && (
          <section data-testid="stage-confidence-transfer" className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/15 dark:to-blue-900/15">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Confidence transfer
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                What's being proven · what's new · what you'll feel
              </p>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <ConfTransferRow
                icon={Award}
                tone="amber"
                label="Proving what"
                body={frame.proving_what}
                testid="ct-proving"
              />
              <ConfTransferRow
                icon={Compass}
                tone="blue"
                label="New environment"
                body={frame.new_environment}
                testid="ct-new-env"
              />
              <ConfTransferRow
                icon={Heart}
                tone="rose"
                label="Confidence to feel"
                body={frame.confidence_earned}
                testid="ct-confidence"
              />
            </div>
          </section>
        )}

        {/* 4 — REFLECTION */}
        <section data-testid="stage-reflection">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 mb-2">
            <Heart className="w-4 h-4 text-rose-600" />
            Quiet reflection
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
            No forms. Just questions to sit with for a moment. Tap any one that lands.
          </p>
          <div className="space-y-2">
            {(frame.reflection_prompts || []).map((q, i) => {
              const touched = !!reflectionsTouched[i];
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`stage-reflection-${i}`}
                  onClick={() => setReflectionsTouched((s) => ({ ...s, [i]: !touched }))}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-2xl border transition-colors ${
                    touched
                      ? "border-rose-300 bg-rose-50 dark:border-rose-700 dark:bg-rose-900/20"
                      : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-rose-50 dark:hover:bg-rose-900/10"
                  }`}
                >
                  {touched ? (
                    <CheckCircle2 className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm font-semibold leading-snug ${
                    touched ? "text-rose-900 dark:text-rose-200" : "text-slate-800 dark:text-slate-200"
                  }`}>{q}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* 5 — NEXT LESSON CONNECTION */}
        <section data-testid="stage-next-lesson" className="bg-slate-900 dark:bg-black border border-slate-800 rounded-3xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-amber-400" />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-400">Take this into your next lesson</p>
          </div>
          <ul className="space-y-2 mt-2">
            {(frame.next_lesson_focus || []).map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <Lightbulb className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-1" />
                <p className="text-sm text-slate-200 leading-relaxed">{f}</p>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-500 mt-4 italic">
            One thing to remember beats five things you forget.
          </p>
        </section>
      </main>
    </div>
  );
}

function Panel({ icon: Icon, tone, label, body }) {
  const tones = {
    amber:   "border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/15 text-amber-900 dark:text-amber-200",
    blue:    "border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/15 text-blue-900 dark:text-blue-200",
    emerald: "border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/15 text-emerald-900 dark:text-emerald-200",
    rose:    "border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/15 text-rose-900 dark:text-rose-200",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tones[tone] || tones.blue}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-80" />
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</p>
      </div>
      <p className="text-sm font-semibold leading-snug">{body}</p>
    </div>
  );
}

function ConfTransferRow({ icon: Icon, tone, label, body, testid }) {
  const iconColour = {
    amber:   "text-amber-600 dark:text-amber-400",
    blue:    "text-blue-600 dark:text-blue-400",
    rose:    "text-rose-600 dark:text-rose-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
  }[tone] || "text-slate-500";
  return (
    <div data-testid={testid} className="px-4 py-3 flex items-start gap-3">
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${iconColour}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 mb-0.5">{label}</p>
        <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// Lightweight markdown-bold rendering for pre_brief copy.
function linkifyBold(text) {
  if (!text) return "";
  // Escape HTML first
  const esc = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-extrabold">$1</strong>');
}
